export type TestCase = {
  label: string;
  call: string;
  expect: unknown;
};

export type CodeTask = {
  id: string;
  title: string;
  brief: string;
  hint: string;
  starterCode: string;
  tests: TestCase[];
  optional?: boolean;
};

export type Lesson = {
  id: 1 | 2 | 3 | 4 | 5;
  code: string;
  title: string;
  payoff: string;
  briefing: string;
  concepts: string[];
  timeline: { minutes: number; label: string; mode: "talk" | "code" | "team" }[];
  tasks: CodeTask[];
};

export const lessons: Lesson[] = [
  {
    id: 1,
    code: "MISSION 01",
    title: "Het afgesloten technieklab",
    payoff: "Bouw de taal waarmee jouw game kamers, agenten en commando’s begrijpt.",
    briefing: "16:42 — Project Aurora is verdwenen. Alleen een onbekende agent en de spiegelcode 413 zijn op de camera vastgelegd.",
    concepts: ["variabelen", "strings", "functies", "return"],
    timeline: [
      { minutes: 6, label: "Cold open: de USB is verdwenen", mode: "team" },
      { minutes: 6, label: "Live-demo: input → functie → output", mode: "talk" },
      { minutes: 40, label: "Vier checkpoint-sprints", mode: "code" },
      { minutes: 8, label: "Debug duel", mode: "team" },
      { minutes: 18, label: "Bouw je eigen openingsscène of side quest", mode: "code" },
      { minutes: 12, label: "Speeltest en verplichte checkpoints afronden", mode: "team" },
    ],
    tasks: [
      {
        id: "l1-room",
        title: "Kamer scanner",
        brief: "Mission Control ziet niets. Laat de scanner vertellen waar de agent staat en welk voorwerp daar ligt.",
        hint: "Gebruik een f-string: f\"Je bent in de {naam}...\"",
        starterCode: `def beschrijf_kamer(naam, voorwerp):
    # Geef precies deze vorm terug:
    # Je bent in de serverruimte. Je ziet: een knipperende usb-stick.
    return ""
`,
        tests: [
          {
            label: "beschrijft de serverruimte",
            call: "beschrijf_kamer('serverruimte', 'een knipperende usb-stick')",
            expect: "Je bent in de serverruimte. Je ziet: een knipperende usb-stick.",
          },
          {
            label: "werkt ook voor een andere kamer",
            call: "beschrijf_kamer('werkplaats', 'een gesloten kast')",
            expect: "Je bent in de werkplaats. Je ziet: een gesloten kast.",
          },
          {
            label: "gebruikt beide meegegeven waarden",
            call: "beschrijf_kamer('hal', 'rode voetsporen')",
            expect: "Je bent in de hal. Je ziet: rode voetsporen.",
          },
        ],
      },
      {
        id: "l1-badge",
        title: "Agentenpas",
        brief: "Maak een digitale pas. Ruim beide teksten op en zet ze in hoofdletters, zodat de scanner ze accepteert.",
        hint: "Gebruik bij beide waarden .strip().upper() en plaats ze in één f-string.",
        starterCode: `def maak_agentenpas(codenaam, specialisme):
    # Voorbeeld: AGENT: PIXEL | SPECIALISME: ROBOTICA
    return ""
`,
        tests: [
          {
            label: "maakt een geldige pas",
            call: "maak_agentenpas('Pixel', 'robotica')",
            expect: "AGENT: PIXEL | SPECIALISME: ROBOTICA",
          },
          {
            label: "ruimt slordige invoer op",
            call: "maak_agentenpas('  Byte  ', '  drones ')",
            expect: "AGENT: BYTE | SPECIALISME: DRONES",
          },
        ],
      },
      {
        id: "l1-command",
        title: "Commando-decoder",
        brief: "Spelers typen onder druk. Zorg dat spaties en hoofdletters geen probleem zijn voor de game.",
        hint: "Je kunt methodes achter elkaar zetten: tekst.strip().lower()",
        starterCode: `def lees_commando(tekst):
    # Verwijder spaties voor/achter en maak alles kleine letters.
    return tekst
`,
        tests: [
          { label: "ruimt een commando op", call: "lees_commando('  PAK USB  ')", expect: "pak usb" },
          { label: "laat nette invoer heel", call: "lees_commando('open deur')", expect: "open deur" },
          { label: "werkt met een andere opdracht", call: "lees_commando('  Ga LAB')", expect: "ga lab" },
        ],
      },
      {
        id: "l1-log",
        title: "Bewijslogboek",
        brief: "ARGUS wist losse meldingen. Bouw één exact logbericht van tijdstip, kamer en aanwijzing, zodat het bewijs bewaard blijft.",
        hint: "Ruim alle teksten op met .strip(), zet de kamer in hoofdletters en combineer alles in een f-string.",
        starterCode: `def maak_bewijslogboek(kamer, tijdstip, aanwijzing):
    # Voorbeeld: [16:42] LAB: rode voetsporen
    return ""
`,
        tests: [
          { label: "legt het eerste bewijs vast", call: "maak_bewijslogboek('lab', '16:42', 'rode voetsporen')", expect: "[16:42] LAB: rode voetsporen" },
          { label: "ruimt slordige invoer op", call: "maak_bewijslogboek('  hal ', ' 17:03 ', ' open kast  ')", expect: "[17:03] HAL: open kast" },
          { label: "werkt voor nieuw bewijs", call: "maak_bewijslogboek('serverruimte', '17:11', 'usb-signaal')", expect: "[17:11] SERVERRUIMTE: usb-signaal" },
        ],
      },
      {
        id: "l1-side-callsign",
        title: "Side quest: radiosignaal",
        brief: "Zend een korte geheime roepnaam: de eerste drie letters van de codenaam plus de eerste twee van het specialisme, alles in hoofdletters.",
        hint: "Gebruik slicing zoals tekst[:3]. Plak de twee stukjes met + aan elkaar.",
        starterCode: `def maak_roepnaam(codenaam, specialisme):
    # Pixel + robotica wordt PIXRO
    return ""
`,
        optional: true,
        tests: [
          { label: "maakt de roepnaam van Pixel", call: "maak_roepnaam('Pixel', 'robotica')", expect: "PIXRO" },
          { label: "ruimt invoer op", call: "maak_roepnaam('  Byte ', ' drones ')", expect: "BYTDR" },
          { label: "kan met korte woorden omgaan", call: "maak_roepnaam('Q', 'AI')", expect: "QAI" },
        ],
      },
    ],
  },
  {
    id: 2,
    code: "MISSION 02",
    title: "Kraak de beveiliging",
    payoff: "Laat de game beslissen wie naar binnen mag en welke acties gevolgen hebben.",
    briefing: "De pincode is bekend, maar beveiligings-AI ARGUS eist óók een sleutelkaart. Na drie fouten gaat het lab op slot.",
    concepts: ["if / else", "lijsten", "in", "booleans"],
    timeline: [
      { minutes: 5, label: "Recap race", mode: "team" },
      { minutes: 7, label: "Live-demo: Python als portier", mode: "talk" },
      { minutes: 40, label: "Vier beveiligings-checkpoints", mode: "code" },
      { minutes: 8, label: "Menselijke computer", mode: "team" },
      { minutes: 18, label: "Bouw, hacktest of kies de side quest", mode: "code" },
      { minutes: 12, label: "Randgevallen en verplichte checkpoints afronden", mode: "team" },
    ],
    tasks: [
      {
        id: "l2-card",
        title: "Sleutelkaart-check",
        brief: "De labdeur opent alleen als de inventaris van de speler een sleutelkaart bevat.",
        hint: "De uitdrukking 'sleutelkaart' in inventaris geeft al True of False.",
        starterCode: `def kan_lab_openen(inventaris):
    # Geef True of False terug.
    return False
`,
        tests: [
          { label: "opent met sleutelkaart", call: "kan_lab_openen(['zaklamp', 'sleutelkaart'])", expect: true },
          { label: "blijft dicht zonder kaart", call: "kan_lab_openen(['zaklamp', 'usb-kabel'])", expect: false },
          { label: "blijft dicht bij lege tas", call: "kan_lab_openen([])", expect: false },
        ],
      },
      {
        id: "l2-code",
        title: "Pincode-kraker",
        brief: "Geef KLIK! terug bij code 314. Bij ieder ander getal antwoordt ARGUS met FOUT.",
        hint: "Vergelijk met == en zet beide returns in een if/else.",
        starterCode: `def controleer_pincode(code):
    if code == 314:
        return "..."
    else:
        return "..."
`,
        tests: [
          { label: "accepteert 314", call: "controleer_pincode(314)", expect: "KLIK!" },
          { label: "weigert een foute code", call: "controleer_pincode(123)", expect: "FOUT" },
          { label: "een tekstcode telt niet", call: "controleer_pincode('314')", expect: "FOUT" },
        ],
      },
      {
        id: "l2-access",
        title: "Dubbele beveiliging",
        brief: "ARGUS geeft alleen servertoegang als de sleutelkaart aanwezig én de pincode gekraakt is.",
        hint: "Combineer 'sleutelkaart' in inventaris met code_gekraakt door and te gebruiken.",
        starterCode: `def heeft_servertoegang(inventaris, code_gekraakt):
    # Beide voorwaarden moeten waar zijn.
    return False
`,
        tests: [
          { label: "opent met kaart en code", call: "heeft_servertoegang(['sleutelkaart'], True)", expect: true },
          { label: "kaart zonder code is niet genoeg", call: "heeft_servertoegang(['sleutelkaart'], False)", expect: false },
          { label: "code zonder kaart is niet genoeg", call: "heeft_servertoegang(['zaklamp'], True)", expect: false },
        ],
      },
      {
        id: "l2-denial",
        title: "ARGUS antwoordt",
        brief: "Een rood lampje helpt niemand. Laat ARGUS precies vertellen of de sleutelkaart ontbreekt, de pincode fout is of toegang wordt verleend.",
        hint: "Controleer eerst de ontbrekende kaart, daarna de foute code met elif en gebruik else voor succes.",
        starterCode: `def toegangsbericht(heeft_kaart, code_goed):
    # Geef één van de drie exacte berichten terug.
    return ""
`,
        tests: [
          { label: "meldt een ontbrekende kaart", call: "toegangsbericht(False, True)", expect: "SLEUTELKAART ONTBREEKT" },
          { label: "meldt een foute code", call: "toegangsbericht(True, False)", expect: "PINCODE FOUT" },
          { label: "verleent alleen volledige toegang", call: "toegangsbericht(True, True)", expect: "TOEGANG VERLEEND" },
          { label: "controleert de kaart als eerste", call: "toegangsbericht(False, False)", expect: "SLEUTELKAART ONTBREEKT" },
        ],
      },
      {
        id: "l2-side-laser",
        title: "Side quest: laserrooster",
        brief: "De servicetunnel opent alleen voor een blauw beschermpak met minstens 50 procent batterij.",
        hint: "Combineer de kleurvergelijking en batterijgrens met and. Gebruik >= zodat 50 ook telt.",
        starterCode: `def mag_door_laserrooster(pak_kleur, batterij):
    # Geef True of False terug.
    return False
`,
        optional: true,
        tests: [
          { label: "laat het juiste pak door", call: "mag_door_laserrooster('blauw', 80)", expect: true },
          { label: "accepteert precies 50 procent", call: "mag_door_laserrooster('  BLAUW ', 50)", expect: true },
          { label: "weigert een lege batterij", call: "mag_door_laserrooster('blauw', 49)", expect: false },
          { label: "weigert de verkeerde kleur", call: "mag_door_laserrooster('rood', 100)", expect: false },
        ],
      },
    ],
  },
  {
    id: 3,
    code: "BONUS MISSION",
    title: "Het geheime ARGUS-lab",
    payoff: "Kies zelf je uitdaging: tien side quests, nul verplichte checkpoints.",
    briefing: "ARGUS heeft een verborgen trainingsruimte geopend. Alles in deze missie is bonus: kies uit tien experimenten, verdien extra badges en help daarna een crewmate.",
    concepts: ["slicing", "elif", "grensgevallen", "for-loops", "lijsten", "game timer"],
    timeline: [
      { minutes: 8, label: "Kies je bonusroute", mode: "team" },
      { minutes: 8, label: "Demo: slimme Python-trucs", mode: "talk" },
      { minutes: 52, label: "Tien vrije side quests", mode: "code" },
      { minutes: 12, label: "Bonus-bugjacht in duo’s", mode: "team" },
      { minutes: 10, label: "Show & tell", mode: "team" },
    ],
    tasks: [
      {
        id: "l1-bonus-mirror",
        title: "Side quest: spiegelcode",
        brief: "De camera toont alles achterstevoren. Ontcijfer 413 tot de echte pincode en draai ook andere berichten om.",
        hint: "Met tekst[::-1] lees je een string van achter naar voren. Ruim hem eerst op met strip().",
        starterCode: `def ontcijfer_spiegelcode(tekst):
    # Draai de opgeschoonde tekst om.
    return tekst
`,
        optional: true,
        tests: [
          { label: "maakt van 413 de pincode", call: "ontcijfer_spiegelcode('413')", expect: "314" },
          { label: "ontcijfert het doelwit", call: "ontcijfer_spiegelcode('  BSU  ')", expect: "USB" },
        ],
      },
      {
        id: "l2-bonus-alarm",
        title: "Side quest: alarmniveau",
        brief: "Maak ARGUS slimmer: nul fouten is VEILIG, één of twee is WAARSCHUWING en vanaf drie klinkt ALARM.",
        hint: "Controleer eerst >= 3, daarna >= 1 met elif, en gebruik anders else.",
        starterCode: `def alarmstatus(foute_pogingen):
    if foute_pogingen >= 3:
        return "..."
    # Voeg elif en else toe.
`,
        optional: true,
        tests: [
          { label: "geen fouten is veilig", call: "alarmstatus(0)", expect: "VEILIG" },
          { label: "twee fouten geven waarschuwing", call: "alarmstatus(2)", expect: "WAARSCHUWING" },
          { label: "drie fouten activeren alarm", call: "alarmstatus(3)", expect: "ALARM" },
          { label: "meer fouten houden alarm actief", call: "alarmstatus(7)", expect: "ALARM" },
        ],
      },
      {
        id: "l3-bonus-countdown",
        title: "Side quest: noodtimer",
        brief: "Maak de game spannender. Iedere beurt gaat de timer één omlaag; op nul wordt alarm True en lager dan nul mag niet.",
        hint: "Trek eerst 1 af. Gebruik daarna <= 0 om beurten op 0 en alarm op True te zetten.",
        starterCode: `def tik_noodtimer(speler):
    # speler heeft de sleutels beurten en alarm.
    speler["beurten"] -= 1
    # Zet bij nul het alarm aan en voorkom negatieve beurten.
    return speler
`,
        optional: true,
        tests: [
          { label: "telt één beurt af", call: "tik_noodtimer({'beurten': 5, 'alarm': False})", expect: { beurten: 4, alarm: false } },
          { label: "activeert alarm op nul", call: "tik_noodtimer({'beurten': 1, 'alarm': False})", expect: { beurten: 0, alarm: true } },
          { label: "gaat nooit onder nul", call: "tik_noodtimer({'beurten': 0, 'alarm': True})", expect: { beurten: 0, alarm: true } },
        ],
      },
      {
        id: "l3-bonus-radio",
        title: "Side quest: storingsbericht",
        brief: "Een gestoord radiosignaal gebruikt streepjes als spaties. Herstel het bericht en zet het in hoofdletters voor Mission Control.",
        hint: "Ruim eerst de buitenkant op, gebruik .replace('-', ' ') en zet daarna alles in hoofdletters.",
        starterCode: `def herstel_radiosignaal(bericht):
    # "project-aurora-gevonden" wordt "PROJECT AURORA GEVONDEN"
    return bericht
`,
        optional: true,
        tests: [
          { label: "herstelt het hoofdbericht", call: "herstel_radiosignaal('project-aurora-gevonden')", expect: "PROJECT AURORA GEVONDEN" },
          { label: "ruimt ruis aan de randen op", call: "herstel_radiosignaal('  deur-is-open  ')", expect: "DEUR IS OPEN" },
          { label: "werkt met een kort signaal", call: "herstel_radiosignaal('code-314')", expect: "CODE 314" },
        ],
      },
      {
        id: "l3-bonus-power",
        title: "Side quest: energieniveau",
        brief: "Geef de reddingsrobot een batterijmeter: 75 of hoger is VOL, 25 tot en met 74 is LAAG en daaronder is KRITIEK.",
        hint: "Controleer de hoogste grens eerst. Gebruik daarna elif voor 25 en else voor de rest.",
        starterCode: `def energie_status(procent):
    # Geef VOL, LAAG of KRITIEK terug.
    return ""
`,
        optional: true,
        tests: [
          { label: "hoge energie is vol", call: "energie_status(90)", expect: "VOL" },
          { label: "75 telt als vol", call: "energie_status(75)", expect: "VOL" },
          { label: "25 telt als laag", call: "energie_status(25)", expect: "LAAG" },
          { label: "bijna leeg is kritiek", call: "energie_status(24)", expect: "KRITIEK" },
        ],
      },
      {
        id: "l3-bonus-heat",
        title: "Side quest: warmtescanner",
        brief: "De scanner geeft een lijst temperaturen. Tel hoeveel metingen 40 graden of hoger zijn: dat zijn mogelijke indringers of oververhitte machines.",
        hint: "Begin met aantal = 0. Loop door de temperaturen en verhoog aantal bij iedere waarde >= 40.",
        starterCode: `def tel_hittebronnen(temperaturen):
    aantal = 0
    for temperatuur in temperaturen:
        # Tel deze meting als hij 40 of hoger is.
        pass
    return aantal
`,
        optional: true,
        tests: [
          { label: "vindt twee hittebronnen", call: "tel_hittebronnen([22, 41, 39, 52])", expect: 2 },
          { label: "telt de grens van 40 mee", call: "tel_hittebronnen([40, 18, 39])", expect: 1 },
          { label: "kan een lege scan aan", call: "tel_hittebronnen([])", expect: 0 },
          { label: "telt alle warme metingen", call: "tel_hittebronnen([45, 60, 40])", expect: 3 },
        ],
      },
      {
        id: "l3-bonus-fragments",
        title: "Side quest: codefragmenten",
        brief: "ARGUS vond drie losse tekstfragmenten. Ruim ieder fragment op, verbind ze met streepjes en zet de herstelde code in hoofdletters.",
        hint: "Maak een lege lijst, append(fragment.strip()) in een for-loop en gebruik daarna '-'.join(...).upper().",
        starterCode: `def herstel_codefragmenten(fragmenten):
    schone_fragmenten = []
    # Ruim ieder fragment op en bewaar het.
    return ""
`,
        optional: true,
        tests: [
          { label: "herstelt de hoofdcode", call: "herstel_codefragmenten([' aurora ', '314', ' usb '])", expect: "AURORA-314-USB" },
          { label: "werkt met twee fragmenten", call: "herstel_codefragmenten(['deur', ' open '])", expect: "DEUR-OPEN" },
          { label: "werkt met één fragment", call: "herstel_codefragmenten(['  argus  '])", expect: "ARGUS" },
        ],
      },
      {
        id: "l3-bonus-patrol",
        title: "Side quest: dronepatrouille",
        brief: "Laat een drone meerdere rondes langs dezelfde kamers vliegen. Geef één lijst terug met de volledige route in de juiste volgorde.",
        hint: "Python kan een lijst herhalen: kamers * rondes. Bij nul rondes krijg je vanzelf een lege lijst.",
        starterCode: `def bouw_patrouille(kamers, rondes):
    # Herhaal de route het gevraagde aantal rondes.
    return []
`,
        optional: true,
        tests: [
          { label: "bouwt twee rondes", call: "bouw_patrouille(['hal', 'lab'], 2)", expect: ["hal", "lab", "hal", "lab"] },
          { label: "houdt één ronde gelijk", call: "bouw_patrouille(['lab', 'serverruimte'], 1)", expect: ["lab", "serverruimte"] },
          { label: "nul rondes geeft geen route", call: "bouw_patrouille(['hal'], 0)", expect: [] },
        ],
      },
      {
        id: "l3-bonus-signal",
        title: "Side quest: vind het signaal",
        brief: "Zoek in een rij metingen de eerste AURORA-melding. Geef de positie terug; als het signaal ontbreekt, geef je -1 terug.",
        hint: "Gebruik enumerate(metingen). Return de index zodra signaal == 'AURORA' en zet return -1 pas na de loop.",
        starterCode: `def vind_aurora_signaal(metingen):
    for index, signaal in enumerate(metingen):
        # Stop direct bij de eerste AURORA-melding.
        pass
    return -1
`,
        optional: true,
        tests: [
          { label: "vindt het eerste signaal", call: "vind_aurora_signaal(['RUIS', 'AURORA', 'AURORA'])", expect: 1 },
          { label: "kan het signaal vooraan vinden", call: "vind_aurora_signaal(['AURORA', 'RUIS'])", expect: 0 },
          { label: "meldt een ontbrekend signaal", call: "vind_aurora_signaal(['RUIS', 'STILTE'])", expect: -1 },
          { label: "kan een lege reeks aan", call: "vind_aurora_signaal([])", expect: -1 },
        ],
      },
      {
        id: "l3-bonus-checksum",
        title: "Side quest: checksumslot",
        brief: "Tel de codecijfers op. Het slot accepteert de code alleen als het laatste cijfer van de som gelijk is aan het controlegetal.",
        hint: "Gebruik sum(cijfers) en % 10 om alleen het laatste cijfer van de som over te houden.",
        starterCode: `def checksum_klopt(cijfers, controlegetal):
    # Geef True of False terug.
    return False
`,
        optional: true,
        tests: [
          { label: "accepteert een geldige checksum", call: "checksum_klopt([3, 1, 4], 8)", expect: true },
          { label: "weigert een fout controlegetal", call: "checksum_klopt([3, 1, 4], 9)", expect: false },
          { label: "gebruikt alleen het laatste somcijfer", call: "checksum_klopt([9, 8, 7], 4)", expect: true },
          { label: "een lege code heeft checksum nul", call: "checksum_klopt([], 0)", expect: true },
        ],
      },
    ],
  },
  {
    id: 4,
    code: "MISSION 04",
    title: "Start de spelmotor",
    payoff: "Geef de escape-room een geheugen en laat de speler betrouwbaar door het gebouw bewegen.",
    briefing: "De route naar Project Aurora is bekend. Bouw nu één centrale speltoestand die onthoudt waar de speler is, wat die bij zich draagt en welke deuren open zijn.",
    concepts: ["dictionaries", "game state", "lijsten", "mutatie"],
    timeline: [
      { minutes: 7, label: "Teken de game state", mode: "team" },
      { minutes: 7, label: "Live-demo: één bron van waarheid", mode: "talk" },
      { minutes: 40, label: "Vier verplichte checkpoints", mode: "code" },
      { minutes: 10, label: "Menselijke game state", mode: "team" },
      { minutes: 16, label: "Bouw de kaart uit of kies de side quest", mode: "code" },
      { minutes: 10, label: "Route-test en exit ticket", mode: "team" },
    ],
    tasks: [
      {
        id: "l3-state",
        title: "Start de spelmotor",
        brief: "Maak de centrale game state: de speler begint in de hal, met een lege inventaris en een gesloten code-slot.",
        hint: "Geef één dictionary terug met de sleutels kamer, inventaris en code_gekraakt.",
        starterCode: `def maak_speler():
    return {
        "kamer": "...",
        "inventaris": [],
        "code_gekraakt": False,
    }
`,
        tests: [
          {
            label: "maakt de juiste starttoestand",
            call: "maak_speler()",
            expect: { kamer: "hal", inventaris: [], code_gekraakt: false },
          },
          { label: "begint echt met lege inventaris", call: "len(maak_speler()['inventaris'])", expect: 0 },
        ],
      },
      {
        id: "l3-move",
        title: "Verplaats de speler",
        brief: "Verplaats de speler alleen naar een kamer die echt bestaat. Een verzonnen kamer mag de state niet veranderen.",
        hint: "Maak een lijst met geldige kamers en gebruik if nieuwe_kamer in geldige_kamers.",
        starterCode: `def verplaats(speler, nieuwe_kamer):
    geldige_kamers = ["hal", "lab", "serverruimte", "uitgang"]
    # Verander speler["kamer"] alleen als de kamer geldig is.
    return speler
`,
        tests: [
          { label: "gaat naar het lab", call: "verplaats({'kamer': 'hal'}, 'lab')", expect: { kamer: "lab" } },
          { label: "kan naar de uitgang", call: "verplaats({'kamer': 'lab'}, 'uitgang')", expect: { kamer: "uitgang" } },
          { label: "weigert een spookkamer", call: "verplaats({'kamer': 'hal'}, 'maanbasis')", expect: { kamer: "hal" } },
        ],
      },
      {
        id: "l4-pickup",
        title: "Pak de sleutelkaart",
        brief: "De sleutelkaart ligt in het lab. Voeg hem alleen daar toe aan de inventaris en zorg dat hij nooit dubbel verschijnt.",
        hint: "Combineer de juiste kamer met 'sleutelkaart' not in speler['inventaris'] en gebruik daarna append().",
        starterCode: `def pak_sleutelkaart(speler):
    # Pas de inventaris alleen aan als dat mag.
    return speler
`,
        tests: [
          { label: "pakt de kaart in het lab", call: "pak_sleutelkaart({'kamer': 'lab', 'inventaris': []})", expect: { kamer: "lab", inventaris: ["sleutelkaart"] } },
          { label: "pakt niets in de hal", call: "pak_sleutelkaart({'kamer': 'hal', 'inventaris': []})", expect: { kamer: "hal", inventaris: [] } },
          { label: "voegt de kaart niet dubbel toe", call: "pak_sleutelkaart({'kamer': 'lab', 'inventaris': ['sleutelkaart']})", expect: { kamer: "lab", inventaris: ["sleutelkaart"] } },
        ],
      },
      {
        id: "l4-status",
        title: "Statusrapport",
        brief: "Mission Control wil één leesbare regel met de kamer, het aantal spullen en de toestand van het codeslot.",
        hint: "Gebruik len() voor het aantal spullen en kies met if tussen OPEN en DICHT.",
        starterCode: `def maak_statusrapport(speler):
    slot = "..."
    # Voorbeeld: KAMER: lab | SPULLEN: 2 | CODESLOT: OPEN
    return ""
`,
        tests: [
          { label: "toont een open slot", call: "maak_statusrapport({'kamer': 'lab', 'inventaris': ['lamp', 'sleutelkaart'], 'code_gekraakt': True})", expect: "KAMER: lab | SPULLEN: 2 | CODESLOT: OPEN" },
          { label: "toont een gesloten slot", call: "maak_statusrapport({'kamer': 'hal', 'inventaris': [], 'code_gekraakt': False})", expect: "KAMER: hal | SPULLEN: 0 | CODESLOT: DICHT" },
          { label: "telt ieder voorwerp", call: "maak_statusrapport({'kamer': 'serverruimte', 'inventaris': ['a', 'b', 'c'], 'code_gekraakt': False})", expect: "KAMER: serverruimte | SPULLEN: 3 | CODESLOT: DICHT" },
        ],
      },
      {
        id: "l4-side-map",
        title: "Side quest: geheime kamer",
        brief: "Een scan met code AURORA opent vanuit het lab een verborgen controlekamer. Andere codes en kamers mogen niets veranderen.",
        hint: "Gebruik twee voorwaarden met and en verander daarna speler['kamer'].",
        starterCode: `def ontdek_controlekamer(speler, scan_code):
    # Alleen vanuit het lab en met de exacte code AURORA.
    return speler
`,
        optional: true,
        tests: [
          { label: "opent de geheime kamer", call: "ontdek_controlekamer({'kamer': 'lab'}, 'AURORA')", expect: { kamer: "controlekamer" } },
          { label: "weigert een foute code", call: "ontdek_controlekamer({'kamer': 'lab'}, 'ARGUS')", expect: { kamer: "lab" } },
          { label: "werkt alleen vanuit het lab", call: "ontdek_controlekamer({'kamer': 'hal'}, 'AURORA')", expect: { kamer: "hal" } },
        ],
      },
    ],
  },
  {
    id: 5,
    code: "FINAL MISSION",
    title: "Bouw de ontsnapping",
    payoff: "Verbind acties en winst tot een complete escape-room die iemand echt kan uitspelen.",
    briefing: "Project Aurora ligt in de serverruimte en de nooddeur telt af. Programmeer de laatste acties, bewijs wanneer de speler wint en neem daarna de complete game mee.",
    concepts: ["functies koppelen", "lijsten aanpassen", "voorwaarden", "while-loop"],
    timeline: [
      { minutes: 6, label: "Finale briefing", mode: "team" },
      { minutes: 7, label: "Live-demo: acties veranderen state", mode: "talk" },
      { minutes: 40, label: "Vier verplichte final-checkpoints", mode: "code" },
      { minutes: 8, label: "Bug bounty", mode: "team" },
      { minutes: 19, label: "Download, side quest, remix en speeltest", mode: "code" },
      { minutes: 10, label: "Gamepremière", mode: "team" },
    ],
    tasks: [
      {
        id: "l3-action",
        title: "Pak Project Aurora",
        brief: "Pak de usb-stick alleen in de serverruimte en voeg hem nooit dubbel aan de inventaris toe.",
        hint: "Controleer commando, kamer en of 'usb-stick' nog niet in de lijst staat. Gebruik daarna append().",
        starterCode: `def voer_actie_uit(speler, commando):
    if (
        commando == "pak usb"
        and speler["kamer"] == "serverruimte"
        and "usb-stick" not in speler["inventaris"]
    ):
        # Voeg hier de usb-stick toe.
        pass
    return speler
`,
        tests: [
          {
            label: "pakt de usb in de serverruimte",
            call: "voer_actie_uit({'kamer': 'serverruimte', 'inventaris': []}, 'pak usb')",
            expect: { kamer: "serverruimte", inventaris: ["usb-stick"] },
          },
          {
            label: "voegt hem niet dubbel toe",
            call: "voer_actie_uit({'kamer': 'serverruimte', 'inventaris': ['usb-stick']}, 'pak usb')",
            expect: { kamer: "serverruimte", inventaris: ["usb-stick"] },
          },
          {
            label: "kan de usb niet door een muur pakken",
            call: "voer_actie_uit({'kamer': 'hal', 'inventaris': []}, 'pak usb')",
            expect: { kamer: "hal", inventaris: [] },
          },
        ],
      },
      {
        id: "l3-win",
        title: "De winstconditie",
        brief: "De speler wint alleen bij de uitgang, met de usb-stick én een gekraakte pincode.",
        hint: "Combineer drie vergelijkingen met and.",
        starterCode: `def is_ontsnapt(speler):
    # speler bevat: kamer, inventaris en code_gekraakt
    return False
`,
        tests: [
          {
            label: "wint met alle voorwaarden",
            call: "is_ontsnapt({'kamer': 'uitgang', 'inventaris': ['usb-stick'], 'code_gekraakt': True})",
            expect: true,
          },
          {
            label: "wint niet zonder usb",
            call: "is_ontsnapt({'kamer': 'uitgang', 'inventaris': [], 'code_gekraakt': True})",
            expect: false,
          },
          {
            label: "wint niet in de verkeerde kamer",
            call: "is_ontsnapt({'kamer': 'serverruimte', 'inventaris': ['usb-stick'], 'code_gekraakt': True})",
            expect: false,
          },
          {
            label: "wint niet met een gesloten code-slot",
            call: "is_ontsnapt({'kamer': 'uitgang', 'inventaris': ['usb-stick'], 'code_gekraakt': False})",
            expect: false,
          },
        ],
      },
      {
        id: "l5-door",
        title: "Open de serverdeur",
        brief: "Verplaats de speler naar de serverruimte, maar alleen met een sleutelkaart én een gekraakte code.",
        hint: "Controleer de inventaris en code_gekraakt met and. Verander alleen dan de kamer.",
        starterCode: `def open_serverdeur(speler):
    # Verplaats de speler alleen bij dubbele toegang.
    return speler
`,
        tests: [
          { label: "opent met kaart en code", call: "open_serverdeur({'kamer': 'lab', 'inventaris': ['sleutelkaart'], 'code_gekraakt': True})", expect: { kamer: "serverruimte", inventaris: ["sleutelkaart"], code_gekraakt: true } },
          { label: "blijft dicht zonder code", call: "open_serverdeur({'kamer': 'lab', 'inventaris': ['sleutelkaart'], 'code_gekraakt': False})", expect: { kamer: "lab", inventaris: ["sleutelkaart"], code_gekraakt: false } },
          { label: "blijft dicht zonder kaart", call: "open_serverdeur({'kamer': 'lab', 'inventaris': [], 'code_gekraakt': True})", expect: { kamer: "lab", inventaris: [], code_gekraakt: true } },
        ],
      },
      {
        id: "l5-ending",
        title: "Zend het eindrapport",
        brief: "Mission Control wil een glashelder eindbericht: Project Aurora is alleen veilig bij de uitgang, met de usb-stick en een gekraakte code.",
        hint: "Combineer de drie voorwaarden met and. Geef bij succes PROJECT AURORA VEILIG terug en anders MISSIE NOG NIET KLAAR.",
        starterCode: `def maak_eindrapport(speler):
    # Geef één van de twee exacte berichten terug.
    return ""
`,
        tests: [
          { label: "zendt het succesbericht", call: "maak_eindrapport({'kamer': 'uitgang', 'inventaris': ['usb-stick'], 'code_gekraakt': True})", expect: "PROJECT AURORA VEILIG" },
          { label: "waarschuwt zonder usb", call: "maak_eindrapport({'kamer': 'uitgang', 'inventaris': [], 'code_gekraakt': True})", expect: "MISSIE NOG NIET KLAAR" },
          { label: "waarschuwt in de verkeerde kamer", call: "maak_eindrapport({'kamer': 'serverruimte', 'inventaris': ['usb-stick'], 'code_gekraakt': True})", expect: "MISSIE NOG NIET KLAAR" },
          { label: "waarschuwt bij een dicht slot", call: "maak_eindrapport({'kamer': 'uitgang', 'inventaris': ['usb-stick'], 'code_gekraakt': False})", expect: "MISSIE NOG NIET KLAAR" },
        ],
      },
      {
        id: "l5-side-score",
        title: "Side quest: missiescore",
        brief: "Start met 100 punten. Iedere beurt kost 3 punten en iedere hint 10. De score mag nooit onder nul zakken.",
        hint: "Bereken eerst de score en gebruik max(0, score) om negatieve punten te voorkomen.",
        starterCode: `def bereken_missiescore(beurten, hints):
    # Geef een getal van 0 tot en met 100 terug.
    return 0
`,
        optional: true,
        tests: [
          { label: "berekent een snelle missie", call: "bereken_missiescore(5, 1)", expect: 75 },
          { label: "begint op honderd", call: "bereken_missiescore(0, 0)", expect: 100 },
          { label: "zakt nooit onder nul", call: "bereken_missiescore(50, 5)", expect: 0 },
        ],
      },
    ],
  },
];

export const CORE_TASK_COUNT = lessons.flatMap((lesson) => lesson.tasks).filter((task) => !task.optional).length;
export const BONUS_TASK_COUNT = lessons.flatMap((lesson) => lesson.tasks).filter((task) => task.optional).length;
export const CORE_TASK_IDS = new Set(
  lessons.flatMap((lesson) => lesson.tasks.filter((task) => !task.optional).map((task) => task.id)),
);

export const finalGameCode = `"""CodeQuest: De verdwenen USB-stick
Een tekst-escape-room, gebouwd in vier lessen Python verspreid over vijf weken.
"""

KAMERS = {
    "hal": "Een donkere hal. Een toegangsscanner knippert ongeduldig.",
    "lab": "Werkbanken, kabels en een display met de spiegelcode 413.",
    "serverruimte": "Ronkende servers. Project Aurora ligt op een metalen tafel.",
    "uitgang": "De nooddeur. Het scherm vraagt om de usb-stick en de gekraakte code.",
}


def lees_commando(tekst):
    return tekst.strip().lower()


def maak_agentenpas(codenaam, specialisme):
    return f"AGENT: {codenaam.strip().upper()} | SPECIALISME: {specialisme.strip().upper()}"


def ontcijfer_spiegelcode(tekst):
    return tekst.strip()[::-1]


def maak_speler():
    return {
        "kamer": "hal",
        "inventaris": ["sleutelkaart"],
        "code_gekraakt": False,
    }


def controleer_pincode(code):
    if code == 314:
        return "KLIK!"
    return "FOUT"


def heeft_servertoegang(speler):
    return "sleutelkaart" in speler["inventaris"] and speler["code_gekraakt"]


def voer_actie_uit(speler, commando):
    if commando == "ga lab":
        speler["kamer"] = "lab"
        return "Je pas werkt. Je sluipt het technieklab in."
    if commando == "kraak 314" and speler["kamer"] == "lab":
        speler["code_gekraakt"] = True
        return "KLIK! ARGUS geeft de serverdeur vrij."
    if commando == "ga serverruimte" and heeft_servertoegang(speler):
        speler["kamer"] = "serverruimte"
        return "De zware serverdeur schuift open."
    if commando == "pak usb" and speler["kamer"] == "serverruimte":
        if "usb-stick" not in speler["inventaris"]:
            speler["inventaris"].append("usb-stick")
            return "Project Aurora is veilig! Nu naar de uitgang."
        return "Die heb je al."
    if commando == "ga uitgang":
        speler["kamer"] = "uitgang"
        return "Je staat bij de nooddeur."
    return "Dat commando werkt hier niet."


def is_ontsnapt(speler):
    return (
        speler["kamer"] == "uitgang"
        and "usb-stick" in speler["inventaris"]
        and speler["code_gekraakt"]
    )


def speel():
    print("=== PROJECT AURORA: DE VERDWENEN USB-STICK ===")
    codenaam = input("Kies je codenaam: ")
    print(maak_agentenpas(codenaam, "python"))
    print("Spiegelcode gevonden: 413 →", ontcijfer_spiegelcode("413"))
    print("Commando's: ga lab, kraak 314, ga serverruimte, pak usb, ga uitgang")
    speler = maak_speler()

    while not is_ontsnapt(speler):
        print("\\n" + KAMERS[speler["kamer"]])
        print("Inventaris:", speler["inventaris"] or "leeg")
        commando = lees_commando(input("> "))
        print(voer_actie_uit(speler, commando))

    print("\\nMISSIE GESLAAGD! Project Aurora is veiliggesteld.")


if __name__ == "__main__":
    speel()
`;
